import { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell,
  BellRinging,
  ChatCircleText,
  FirstAid,
  Receipt,
  WarningCircle,
  CalendarCheck,
  Camera,
  Sparkle,
  Sun,
  Flag,
  At,
} from "@phosphor-icons/react";
import useNotificationCenter from "@/lib/hooks/useNotificationCenter";
import {
  describeNotification,
  relTime,
  groupNotifications,
  NOTIFICATION_GROUPS,
} from "@/lib/notificationLinks";
import { NOTIFICATION_MANAGE_LINKS } from "@/lib/notificationManageLinks";
import { useAuth } from "@/context/AuthContext";

const ICON_MAP = {
  Bell, BellRinging, ChatCircleText, FirstAid, Receipt,
  WarningCircle, CalendarCheck, Camera, Sparkle, Sun, Flag, At,
};

/**
 * Bell icon + dropdown panel. Used in both MobileShell and DesktopShell.
 *
 * Props:
 *   variant: 'mobile' | 'desktop' — adjusts anchor + dropdown placement.
 *
 * Role behavior:
 *   * Parent/Teacher/CMS — caller-scoped rows. Panel grouped by kind.
 *   * Admin — active-organization delivery rows. Same grouped UI.
 */
export default function NotificationBell({ variant = "mobile" }) {
  const { profile, role } = useAuth();
  const { rows, unread, markSeen, refresh } = useNotificationCenter(
    profile?.id,
    role,
    profile?.organization_id,
  );
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const markSeenTimerRef = useRef(null);
  const panelId = `notification-bell-panel-${useId().replace(/:/g, "")}`;
  const manageLink = NOTIFICATION_MANAGE_LINKS[role] || null;

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (markSeenTimerRef.current) {
      clearTimeout(markSeenTimerRef.current);
      markSeenTimerRef.current = null;
    }
    if (next) {
      refresh();
      // Mark seen shortly after open so the badge clears visibly.
      markSeenTimerRef.current = setTimeout(() => {
        markSeenTimerRef.current = null;
        markSeen();
      }, 250);
    }
  };

  useEffect(() => () => {
    if (markSeenTimerRef.current) clearTimeout(markSeenTimerRef.current);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const grouped = groupNotifications(rows);

  // Dropdown placement — mobile pins to viewport, desktop anchors under bell.
  const panelClass =
    variant === "mobile"
      ? "fixed right-3 top-14 z-50 w-[22rem] max-w-[calc(100vw-1.5rem)] origin-top-right rounded-2xl border border-neutral-200 bg-white shadow-xl"
      : "absolute right-0 top-full mt-2 z-50 w-[22rem] origin-top-right rounded-2xl border border-neutral-200 bg-white shadow-xl";

  const containerClass = variant === "mobile" ? "relative" : "relative";
  const buttonSizeClass = "h-11 w-11";

  return (
    <div ref={panelRef} className={containerClass} data-testid="notification-bell-root">
      <button
        type="button"
        data-testid="notification-bell-button"
        data-unread={unread}
        aria-label={unread > 0 ? `${unread} new notifications` : "Notifications"}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="menu"
        onClick={toggle}
        className={`relative inline-flex ${buttonSizeClass} items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-600 transition hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`}
      >
        <Bell size={17} weight="duotone" />
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            data-count={unread}
            className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#C6851E] px-1 text-[10px] font-bold text-white ring-2 ring-white"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-bell-panel"
          id={panelId}
          role="menu"
          className={panelClass}
        >
          <header className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                Notifications
              </p>
              <p className="font-heading text-sm font-semibold text-neutral-900">
                {rows.length === 0 ? "All caught up" : `Last ${rows.length}`}
              </p>
            </div>
            {manageLink && (
              <Link
                to={manageLink.to}
                onClick={() => setOpen(false)}
                data-testid="notification-bell-manage"
                className="text-xs font-semibold text-primary-hover hover:underline"
              >
                {manageLink.label}
              </Link>
            )}
          </header>

          {rows.length === 0 ? (
            <div
              data-testid="notification-bell-empty"
              className="px-4 py-10 text-center"
            >
              <Bell size={28} weight="duotone" className="mx-auto text-neutral-300" />
              <p className="mt-2 text-sm font-semibold text-neutral-700">
                Nothing here yet
              </p>
              <p className="mt-1 text-xs text-neutral-500">
                New messages, invoices, and events will show up here.
              </p>
            </div>
          ) : (
            <div className="max-h-[28rem] overflow-y-auto">
              {NOTIFICATION_GROUPS.map((g) => {
                const items = grouped[g.key] || [];
                if (items.length === 0) return null;
                return (
                  <div
                    key={g.key}
                    data-testid={`notification-bell-group-${g.key}`}
                    className="border-b border-neutral-100 last:border-b-0"
                  >
                    <p className="px-4 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-500">
                      {g.label}
                    </p>
                    <ul>
                      {items.map((row) => (
                        <NotificationItem
                          key={row.id}
                          row={row}
                          role={role}
                          onClose={() => setOpen(false)}
                          onNavigate={(path) => navigate(path)}
                        />
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationItem({ row, role, onClose, onNavigate }) {
  const { icon, label, path, statusTone, isFailure, detail } = describeNotification(row, role);
  const Icon = ICON_MAP[icon] || Bell;

  const clickable = !!path;

  const inner = (
    <>
      {row.is_unread && (
        <span
          aria-hidden="true"
          className="absolute left-1.5 top-4 h-1.5 w-1.5 rounded-full bg-[#C6851E]"
        />
      )}
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl ${
          isFailure ? "bg-[#C6851E]/10 text-[#C6851E]" : "bg-primary/10 text-primary-hover"
        }`}
      >
        <Icon size={16} weight="duotone" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p className="truncate text-sm font-semibold text-neutral-900">{label}</p>
          {role === "admin" && (
            <span
              className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide ${statusTone}`}
            >
              {row.status}
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-neutral-600">{detail || row.subject || "—"}</p>
        <p className="mt-0.5 text-[10px] text-neutral-400">{relTime(row.created_at)}</p>
      </div>
    </>
  );

  // Clickable → real <a href> via React Router Link. This gives us:
  //   * screen-reader `link` role (not "clickable generic").
  //   * Cmd/Ctrl-click + middle-click natively open in a new tab,
  //     critical for admins triaging multiple notifications.
  //   * SPA navigation preserved on plain click via Link's internal
  //     event handler — no full reload.
  // We DON'T close the dropdown on modifier-click, so the admin keeps
  // the bell open while farming out tabs.
  if (clickable) {
    const handleClick = (e) => {
      // Let the browser handle modifier clicks natively (new tab/window).
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
        return;
      }
      // Plain click: close the dropdown, SPA-navigate ourselves (path
      // already resolves to the correct role-prefixed route).
      e.preventDefault();
      onClose();
      onNavigate(path);
    };
    return (
      <li
        data-testid="notification-bell-item"
        data-kind={row.kind}
        data-unread={row.is_unread ? "true" : "false"}
        data-status={row.status}
        data-link={path}
        className={`relative ${row.is_unread ? "bg-primary/[0.03]" : ""}`}
      >
        <Link
          to={path}
          onClick={handleClick}
          aria-label={`${label} — ${row.subject || "notification"}`}
          className="relative flex items-start gap-3 px-4 py-3 transition hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {inner}
        </Link>
      </li>
    );
  }

  return (
    <li
      data-testid="notification-bell-item"
      data-kind={row.kind}
      data-unread={row.is_unread ? "true" : "false"}
      data-status={row.status}
      data-link=""
      className={`relative flex items-start gap-3 px-4 py-3 ${row.is_unread ? "bg-primary/[0.03]" : ""}`}
    >
      {inner}
    </li>
  );
}
